// containers/CreateEnterpriseUser/CreateEnterpriseUser.service.ts
import { useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import * as yup from "yup";
import {
  validateForm,
  getTotalRecordsErrorMessage,
} from "./CreateEnterpriseUser.helper";
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, Roles } from "common";
import {
  useLazyGetUsersInvitationsQuery,
  useInviteUserMutation,
  useCreateEnterpriseUserMutation,
  useRequestChangePasswordMutation,
  useGetUsersInvitationsQuery,
} from "redux/services/identity";
import {
  useGetUsersLazyQuery,
  useGetUserMutation,
  useUpdateOrganisationMutation,
  useGetOrganizationQuery,
  useGetProfileQuery,
} from "redux/services/membership";
import { getOrganisationMembership } from "utils";
import { setError } from "redux/slices/errorSlice";

export function useCreateEnterpriseUserService(
  setCloseModalCreate: () => void
) {
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();
  const toast = useToast();

  const lastFormDataRef = useRef<any>(null);

  const [triggerGetUsersRole] = useGetUsersLazyQuery();
  const [triggerGetUsersInvitations] = useLazyGetUsersInvitationsQuery();
  const [createEnterpriseUser, { error: errorCreateEnterpriseUser }] =
    useCreateEnterpriseUserMutation();
  const [inviteUser, { error: errorInviteUser }] = useInviteUserMutation();

  const [getUser] = useGetUserMutation();
  const [updateOrganisation, { error: errorUpdateOrgranisation }] =
    useUpdateOrganisationMutation();
  const [requestChangePasswordMutation, { error: errorRequestChangePassword }] =
    useRequestChangePasswordMutation();

  const { data: profileData } = useGetProfileQuery();
  const membershipOrg = getOrganisationMembership(
    profileData?.data?.memberships
  );
  const isPaymentOps: boolean =
    profileData?.data?.listRoles?.includes(Roles.PaymentOps) || false;
  const { data: orgData, isFetching: isFetchingOrgData } =
    useGetOrganizationQuery(
      {
        orgId: membershipOrg?.organisationId ?? "",
        userId: profileData?.data?.userId ?? "",
      },
      { skip: !profileData || isPaymentOps }
    );

  const defaultParamsGetPendingUsers = useMemo(
    () => ({
      pageNumber: DEFAULT_PAGE,
      pageSize: DEFAULT_PAGE_SIZE,
      orgToken: membershipOrg?.token ?? "",
    }),
    [membershipOrg?.token]
  );

  const { refetch: refetchGetPendingUsers } = useGetUsersInvitationsQuery(
    defaultParamsGetPendingUsers,
    {
      refetchOnMountOrArgChange: false,
      skip: isPaymentOps,
    }
  );

  const { refetch: refetchGetPendingMakers } = useGetUsersInvitationsQuery(
    {
      ...defaultParamsGetPendingUsers,
      userRole: [Roles.BUMaker],
    },
    {
      refetchOnMountOrArgChange: false,
      skip: isPaymentOps,
    }
  );

  const { refetch: refetchGetPendingCheckers } = useGetUsersInvitationsQuery(
    {
      ...defaultParamsGetPendingUsers,
      userRole: [Roles.BUChecker],
    },
    {
      refetchOnMountOrArgChange: false,
      skip: isPaymentOps,
    }
  );

  const { refetch: refetchGetPendingApprovers } = useGetUsersInvitationsQuery(
    {
      ...defaultParamsGetPendingUsers,
      userRole: [Roles.BUApprover],
    },
    {
      refetchOnMountOrArgChange: false,
      skip: isPaymentOps,
    }
  );

  const refetchAllUsers = () =>
    Promise.all([
      refetchGetPendingUsers(),
      refetchGetPendingMakers(),
      refetchGetPendingCheckers(),
      refetchGetPendingApprovers(),
    ]);

  const finishRegisterUser = async () => {
    setCloseModalCreate();

    toast({
      title: "Enterprise User registered successfully.",
      status: "success",
      duration: 6000,
      isClosable: true,
      position: "top-right",
    });

    if (!isPaymentOps) {
      await refetchAllUsers();
    }
  };

  const afterCreateUser = async (createRes: any) => {
    if (createRes.data) {
      const userData = await getUser({
        userId: createRes.data.uuid,
      }).unwrap();

      if (userData) {
        const formData = lastFormDataRef.current;
        await updateOrganisation({
          userId: userData.data.userId,
          orgId: userData.data.memberships[0].organisationId,
          lineOfBusiness: "Enterprise",
          orgType: "PCL",
          companyName: formData.bussinessUnitName,
          leiNumber: formData.bussinessUnitCode,
          tradingName: formData.paymentMethod,
          companyNumber: formData.serviceProviderCode,
          externalIdentityList: [
            {
              externalSystem: "TYMEX",
              externalId: formData.bussinessUnitCode,
            },
          ],
        }).unwrap();

        await requestChangePasswordMutation({
          userId: createRes.data.uuid,
        }).unwrap();

        finishRegisterUser();
      }
    }
  };

  const handleSubmit = async (formData: any, schema: yup.ObjectSchema<any>) => {
    lastFormDataRef.current = formData;

    const { isValid } = await validateForm(schema, formData);
    if (!isValid) return;

    try {
      setIsLoading(true);
      if (isPaymentOps) {
        const payload = { ...formData };
        delete payload.payrollManagedBy;

        const createRes = await createEnterpriseUser(payload).unwrap();

        await afterCreateUser(createRes);

        return;
      }

      const role = formData.userRole ?? "";
      const resActive = await triggerGetUsersRole({
        pageNum: DEFAULT_PAGE,
        pageSize: DEFAULT_PAGE_SIZE,
        listUserRoles: role,
        lockStatus: "false",
        orgId: orgData?.data?.orgId ?? "",
      }).unwrap();

      const resPending = await triggerGetUsersInvitations({
        pageNumber: DEFAULT_PAGE,
        pageSize: DEFAULT_PAGE_SIZE,
        orgToken: membershipOrg?.token,
        userRole: [role],
      }).unwrap();

      const totalRecords =
        (resActive.paging?.totalRecords ?? 0) +
        (resPending.paging?.totalRecords ?? 0);

      const msg1 = getTotalRecordsErrorMessage(role, totalRecords);

      if (msg1) {
        dispatch(setError({ message: msg1, type: "registerUserError" }));
        history.push("/enterprise-users");
        setCloseModalCreate();
        return;
      }

      await inviteUser([
        {
          emailAddress: formData.emailAddress,
          firstName: formData.firstName,
          lastName: formData.lastName,
          organisationId: orgData?.data?.orgId,
          orgToken: membershipOrg?.token,
          memberships: [
            {
              roleName: "OrganisationUser",
              organisationId: orgData?.data?.orgId ?? "",
            },
          ],
          roles: formData.userRole ? [formData.userRole] : [],
        },
      ]).unwrap();

      finishRegisterUser();
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleSubmit,
    isLoading: isLoading,
    isFetchingOrgData,
    error:
      errorCreateEnterpriseUser ||
      errorInviteUser ||
      errorUpdateOrgranisation ||
      errorRequestChangePassword,
  };
}
